import React, {useEffect, useRef, useState} from 'react';
import {ArrowLeft, ArrowRight, Compass, Search} from 'lucide-react';
import Layout from "../Layout";

// Animation styles to add to the head
const animationStyles = `
.fade-in {
  animation: fadeIn 0.8s ease-out;
}

.slide-in-left {
  animation: slideInLeft 0.6s ease-out;
}

.slide-in-right {
  animation: slideInRight 0.6s ease-out;
}

.bounce-in {
  animation: bounceIn 0.8s ease-out;
}

.scale-in {
  animation: scaleIn 0.5s ease-out;
}

.float {
  animation: float 3s ease-in-out infinite;
}

.glow {
  animation: glow 2s ease-in-out infinite alternate;
}

.pulse-animation {
  animation: pulse 2s infinite;
}

.typing-animation {
  overflow: hidden;
  border-right: 3px solid #f97316;
  white-space: nowrap;
  animation: typing 3s steps(40, end), blink-caret 0.75s step-end infinite;
}

.shimmer {
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
  animation: shimmer 2s infinite;
}

.rotate-in {
  animation: rotateIn 0.6s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes slideInLeft {
  from { opacity: 0; transform: translateX(-50px); }
  to { opacity: 1; transform: translateX(0); }
}

@keyframes slideInRight {
  from { opacity: 0; transform: translateX(50px); }
  to { opacity: 1; transform: translateX(0); }
}

@keyframes bounceIn {
  0% { opacity: 0; transform: scale(0.3); }
  50% { opacity: 1; transform: scale(1.05); }
  70% { transform: scale(0.9); }
  100% { opacity: 1; transform: scale(1); }
}

@keyframes scaleIn {
  from { opacity: 0; transform: scale(0.8); }
  to { opacity: 1; transform: scale(1); }
}

@keyframes float {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
}

@keyframes glow {
  from { box-shadow: 0 0 20px rgba(249, 115, 22, 0.3); }
  to { box-shadow: 0 0 30px rgba(249, 115, 22, 0.6); }
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
}

@keyframes typing {
  from { width: 0; }
  to { width: 100%; }
}

@keyframes blink-caret {
  from, to { border-color: transparent; }
  50% { border-color: #f97316; }
}

@keyframes shimmer {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

@keyframes rotateIn {
  from { opacity: 0; transform: rotate(-180deg) scale(0.5); }
  to { opacity: 1; transform: rotate(0deg) scale(1); }
}
`;

const CareerMeterComponent = () => {
    // States
    const [currentSection, setCurrentSection] = useState('welcome'); // welcome, assessment, results, allCareers
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [answers, setAnswers] = useState([]);
    const [careerScores, setCareerScores] = useState({});
    const [selectedCareerCategory, setSelectedCareerCategory] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [matchScore, setMatchScore] = useState(0);
    const [currentDisplayScore, setCurrentDisplayScore] = useState(0);
    const [sortedCareers, setSortedCareers] = useState([]);
    const [progressWidth, setProgressWidth] = useState(6.67);
    const [typingAnimation, setTypingAnimation] = useState(true);

    // References
    const scoreIntervalRef = useRef(null);

    // Add animation styles to the document
    useEffect(() => {
        const styleEl = document.createElement('style');
        styleEl.innerHTML = animationStyles;
        document.head.appendChild(styleEl);

        return () => {
            document.head.removeChild(styleEl);
        };
    }, []);

    // Assessment data
    const questions = [
        {
            text: "Which school subjects do you find most interesting and engaging?",
            options: ["Math & Physics", "Biology & Chemistry", "English & Literature", "History & Social Studies", "Computer Science & Technology"]
        },
        {
            text: "What type of activities do you enjoy most in your free time?",
            options: ["Reading and writing", "Building or fixing things", "Playing sports or being active", "Drawing, music, or creative arts", "Playing video games or coding"]
        },
        {
            text: "When working on group projects, what role do you naturally take?",
            options: ["The leader who organizes everyone", "The researcher who finds information", "The creative one with ideas", "The presenter who explains to others", "The detail person who checks everything"]
        },
        {
            text: "What kind of environment helps you focus and learn best?",
            options: ["Quiet library or study room", "Busy classroom with discussions", "Outdoor or hands-on settings", "Creative spaces with art supplies", "Tech labs with computers"]
        },
        {
            text: "Which of these future goals excites you most?",
            options: ["Helping people solve their problems", "Discovering new things or inventions", "Creating beautiful or entertaining content", "Running my own business", "Making a positive impact on society"]
        },
        {
            text: "What type of college program sounds most appealing to you?",
            options: ["Engineering or technical programs", "Medical or health sciences", "Business or economics", "Arts, design, or communications", "Liberal arts or social sciences"]
        },
        {
            text: "How do you prefer to solve problems?",
            options: ["Using logic and step-by-step analysis", "Brainstorming creative solutions", "Researching what others have done", "Discussing with friends and teachers", "Trial and error until it works"]
        },
        {
            text: "What motivates you to do your best work?",
            options: ["Getting good grades and recognition", "Learning something new and interesting", "Helping my friends or family", "Competing and winning", "Creating something I'm proud of"]
        },
        {
            text: "Which extracurricular activities interest you most?",
            options: ["Science club, math team, or robotics", "Student government or debate team", "Art club, drama, or music", "Sports teams or fitness activities", "Volunteer work or community service"]
        },
        {
            text: "When you imagine your future workplace, what do you see?",
            options: ["Modern office with latest technology", "Hospital, clinic, or research lab", "Creative studio or workshop", "Outdoor locations or travel", "School, community center, or helping others"]
        },
        {
            text: "What's most important to you in a future career?",
            options: ["Good salary and job security", "Helping people and making a difference", "Being creative and expressing myself", "Having flexibility and freedom", "Gaining respect and recognition"]
        },
        {
            text: "How do you like to spend your summer breaks?",
            options: ["Taking advanced courses or camps", "Working part-time jobs", "Traveling and exploring", "Pursuing hobbies and interests", "Volunteering in my community"]
        },
        {
            text: "Which type of challenges do you enjoy most?",
            options: ["Math problems and puzzles", "Writing essays and stories", "Building or creating projects", "Organizing events or activities", "Learning about people and cultures"]
        },
        {
            text: "What do your teachers and parents say you're naturally good at?",
            options: ["Analyzing and solving complex problems", "Communicating and explaining things clearly", "Being creative and artistic", "Leading and organizing others", "Being caring and helpful to others"]
        },
        {
            text: "After graduation, what timeline sounds most realistic for your career goals?",
            options: ["4-year college degree, then start career", "2-year program or trade school", "Start working and learn on the job", "Long education path (6+ years) for specialized career", "Take gap year to explore, then decide"]
        }
    ];

    // Comprehensive career database
    const careerDetails = {
        "Software Developer": {
            category: "Technology",
            description: "Design, develop, and maintain software applications, websites, and mobile apps. Work with programming languages like Python, Java, JavaScript, and frameworks to create digital solutions for businesses and consumers.",
            qualifications: "12th Science (PCM) → B.Tech/B.E. in Computer Science, IT, or Software Engineering. Alternative: BCA → MCA. Entrance: JEE Main/Advanced, state CETs, or direct admission",
            skills: ["Programming Languages", "Problem-solving", "Logical thinking", "Software frameworks", "Database management"],
            workEnvironment: "IT companies, startups, tech giants like TCS, Infosys, Google, Microsoft. Remote work common. Collaborative team environment with flexible hours.",
            careerGrowth: "Junior Developer → Senior Developer → Team Lead → Technical Architect → CTO. Can specialize in web, mobile, AI/ML, or blockchain development.",
            industryOutlook: "Excellent - India's IT sector growing rapidly. High demand for skilled developers. Opportunities in fintech, edtech, and emerging technologies."
        },
        "Data Scientist": {
            category: "Technology",
            description: "Extract insights from large datasets using statistical analysis, machine learning, and data visualization. Help businesses make data-driven decisions by identifying patterns, trends, and predictions from complex data.",
            qualifications: "12th Science (PCM) → B.Tech/B.Sc. in Computer Science, Statistics, Mathematics, or Data Science. Advanced: M.Tech/M.Sc. in Data Science. Certifications in Python, R, SQL essential",
            skills: ["Statistics & Mathematics", "Python/R Programming", "Machine Learning", "Data Visualization", "Business Analytics", "SQL"],
            workEnvironment: "Tech companies, consulting firms, banks, e-commerce, healthcare. Mix of individual analysis and team collaboration. Data-driven decision making environment.",
            careerGrowth: "Data Analyst → Data Scientist → Senior Data Scientist → Lead Data Scientist → Chief Data Officer. Specializations in AI/ML, business intelligence, or research.",
            industryOutlook: "Excellent - Fastest growing field in India. High demand across industries. Government initiatives like Digital India driving growth."
        },
        "Cybersecurity Analyst": {
            category: "Technology",
            description: "Protect organizations from cyber threats by monitoring networks, investigating security incidents, implementing security protocols, and ensuring compliance with security standards and regulations.",
            qualifications: "12th Science (PCM) → B.Tech in Cybersecurity, Computer Science, or IT. Certifications: CEH, CISSP, CompTIA Security+. Advanced: M.Tech in Cybersecurity",
            skills: ["Network Security", "Ethical Hacking", "Risk Assessment", "Incident Response", "Security Tools", "Compliance Knowledge"],
            workEnvironment: "IT companies, banks, government agencies, consulting firms. 24/7 monitoring roles common. High-pressure environment during security incidents.",
            careerGrowth: "Security Analyst → Senior Analyst → Security Consultant → Security Manager → CISO. Specializations in penetration testing, forensics, or compliance.",
            industryOutlook: "Excellent - Critical shortage of cybersecurity professionals in India. Government focus on digital security creating massive opportunities."
        }
        // Other career details would go here in the real implementation
    };

    // Career categories
    const careers = {
        "Technology": [
            "Software Developer", "Data Scientist", "Cybersecurity Analyst", "UX/UI Designer", "DevOps Engineer",
            "Machine Learning Engineer", "Cloud Architect", "Mobile App Developer", "Game Developer", "AI Researcher"
        ],
        "Healthcare": [
            "Doctor", "Nurse", "Pharmacist", "Physical Therapist", "Dentist", "Veterinarian", "Medical Researcher",
            "Radiologist", "Surgeon", "Psychiatrist", "Nutritionist"
        ],
        "Business & Finance": [
            "Financial Analyst", "Investment Banker", "Accountant", "Management Consultant", "Marketing Manager",
            "Sales Representative", "Business Analyst", "Project Manager", "Human Resources Manager"
        ],
        "Entrepreneurship & Startups": [
            "Tech Startup Founder", "E-commerce Entrepreneur", "Social Media Influencer", "App Developer & Publisher",
            "Online Course Creator", "Dropshipping Business Owner", "YouTube Content Creator"
        ],
        "Education": [
            "Teacher", "Professor", "School Principal", "Education Administrator", "Curriculum Developer",
            "Educational Psychologist", "Librarian", "Tutor", "Training Specialist"
        ],
        "Creative Arts": [
            "Graphic Designer", "Photographer", "Writer", "Musician", "Actor", "Film Director", "Animator",
            "Interior Designer", "Fashion Designer", "Art Director"
        ],
        "Engineering": [
            "Civil Engineer", "Mechanical Engineer", "Electrical Engineer", "Chemical Engineer", "Aerospace Engineer",
            "Environmental Engineer", "Industrial Engineer", "Petroleum Engineer"
        ],
        "Legal": [
            "Lawyer", "Judge", "Paralegal", "Legal Assistant", "Court Reporter", "Legal Consultant",
            "Corporate Counsel", "Public Defender", "Prosecutor"
        ],
        "Social Services": [
            "Social Worker", "Counselor", "Community Organizer", "Non-profit Director", "Case Manager",
            "Rehabilitation Counselor", "Substance Abuse Counselor", "Family Therapist"
        ]
        // Other categories would go here in the real implementation
    };

    // Initialize career scores
    const initializeCareerScores = () => {
        const scores = {};
        Object.values(careers).flat().forEach(career => {
            scores[career] = 0;
        });
        setCareerScores(scores);
    };

    // Start the assessment
    const startAssessment = () => {
        initializeCareerScores();
        setCurrentSection('assessment');
        setCurrentQuestion(0);
        setAnswers([]);
    };

    // Select an option for the current question
    const selectOption = (index, event) => {
        const newAnswers = [...answers];
        newAnswers[currentQuestion] = index;
        setAnswers(newAnswers);
    };

    // Go to the previous question
    const previousQuestion = () => {
        if (currentQuestion > 0) {
            setCurrentQuestion(currentQuestion - 1);
            setTypingAnimation(true);
        }
    };

    // Go to the next question
    const nextQuestion = () => {
        if (answers[currentQuestion] !== undefined) {
            if (currentQuestion < questions.length - 1) {
                setCurrentQuestion(currentQuestion + 1);
                setTypingAnimation(true);
            } else {
                calculateResults();
            }
        }
    };

    // Update the progress bar when the question changes
    useEffect(() => {
        const progress = ((currentQuestion + 1) / questions.length) * 100;
        setProgressWidth(progress);

        // Reset typing animation after component renders
        if (typingAnimation) {
            setTimeout(() => {
                setTypingAnimation(false);
            }, 10);
        }
    }, [currentQuestion, typingAnimation]);

    // Scoring matrix for career matching
    const scoringMatrix = {
        0: { // School subjects preference
            0: ["Software Developer", "Data Scientist", "Engineer", "Mathematician", "Physicist"],
            1: ["Doctor", "Nurse", "Pharmacist", "Veterinarian", "Biologist", "Medical Researcher"],
            2: ["Writer", "Journalist", "Teacher", "Lawyer", "Editor", "Content Creator"],
            3: ["Social Worker", "Historian", "Political Scientist", "Anthropologist", "Psychologist"],
            4: ["Software Developer", "Cybersecurity Analyst", "Game Developer", "IT Project Manager"]
        },
        1: { // Free time activities
            0: ["Writer", "Journalist", "Editor", "Librarian", "Research Scientist", "Professor"],
            1: ["Mechanical Engineer", "Civil Engineer", "Electrician", "Carpenter", "Architect"],
            2: ["Athletic Coach", "Physical Therapist", "Sports Medicine Doctor", "Fitness Instructor"],
            3: ["Graphic Designer", "Artist", "Musician", "Photographer", "Interior Designer"],
            4: ["Game Developer", "Software Developer", "UX/UI Designer", "Tech Startup Founder"]
        }
        // More matrix items would be included in the real implementation
    };

    // Calculate career results
    const calculateResults = () => {
        // Reset scores
        initializeCareerScores();

        // Calculate scores based on answers
        answers.forEach((answer, questionIndex) => {
            if (scoringMatrix[questionIndex] && scoringMatrix[questionIndex][answer]) {
                scoringMatrix[questionIndex][answer].forEach(career => {
                    // Find exact career matches in database
                    Object.values(careers).flat().forEach(dbCareer => {
                        if (dbCareer.toLowerCase().includes(career.toLowerCase()) ||
                            career.toLowerCase().includes(dbCareer.toLowerCase()) ||
                            dbCareer === career) {
                            if (careerScores[dbCareer] !== undefined) {
                                setCareerScores(prev => ({
                                    ...prev,
                                    [dbCareer]: prev[dbCareer] + 15
                                }));
                            }
                        }
                    });
                });
            }
        });

        // Sort careers by score and get top matches
        setTimeout(() => {
            const sorted = Object.entries(careerScores)
                .sort(([, a], [, b]) => b - a)
                .slice(0, 9); // Top 9 recommendations

            setSortedCareers(sorted);

            // Calculate match score for display
            const topScore = sorted[0]?.[1] || 0;
            const calculatedMatchScore = Math.min(Math.round((topScore / 100) * 100), 95);
            setMatchScore(calculatedMatchScore);

            // Show results section
            setCurrentSection('results');

            // Animate match score
            if (scoreIntervalRef.current) {
                clearInterval(scoreIntervalRef.current);
            }

            setCurrentDisplayScore(0);
            scoreIntervalRef.current = setInterval(() => {
                setCurrentDisplayScore(prev => {
                    const next = prev + 3;
                    if (next >= calculatedMatchScore) {
                        clearInterval(scoreIntervalRef.current);
                        return calculatedMatchScore;
                    }
                    return next;
                });
            }, 30);
        }, 1000);
    };

    // Find the category for a career
    const findCareerCategory = (career) => {
        for (const [category, careerList] of Object.entries(careers)) {
            if (careerList.includes(career)) {
                return category;
            }
        }
        return "Other";
    };

    // Get emoji for a career category
    const getCareerEmoji = (category) => {
        const emojis = {
            "Technology": "💻",
            "Healthcare": "🏥",
            "Business & Finance": "💼",
            "Entrepreneurship & Startups": "🚀",
            "Education": "📚",
            "Creative Arts": "🎨",
            "Engineering": "⚙️",
            "Science & Research": "🔬",
            "Legal": "⚖️",
            "Media & Communications": "📺",
            "Social Services": "🤝",
            "Skilled Trades": "🔧",
            "Transportation": "🚗",
            "Hospitality & Tourism": "🏨",
            "Sports & Fitness": "🏃",
            "Agriculture": "🌾",
            "Government & Public Service": "🏛️"
        };
        return emojis[category] || "💼";
    };

    // Retake the assessment
    const retakeAssessment = () => {
        setCurrentQuestion(0);
        setAnswers([]);
        setCurrentSection('assessment');
        setTypingAnimation(true);
    };

    // Explore all careers
    const exploreAllCareers = () => {
        setCurrentSection('allCareers');
        setSelectedCareerCategory('all');
        setSearchTerm('');
    };

    // Filter careers by category
    const filterByCategory = (category) => {
        setSelectedCareerCategory(category);
    };

    // Handle search term changes
    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    // Go back to results from all careers view
    const goBackToResults = () => {
        setCurrentSection('results');
    };

    // Get filtered careers for the all careers view
    const getFilteredCareers = () => {
        // First filter by category
        let filteredList = [];
        if (selectedCareerCategory === 'all') {
            // Get all careers with their categories
            filteredList = Object.entries(careers).flatMap(([category, careerList]) =>
                careerList.map(career => ({career, category}))
            );
        } else {
            // Get careers from the selected category
            filteredList = (careers[selectedCareerCategory] || []).map(career => ({
                career,
                category: selectedCareerCategory
            }));
        }

        // Then filter by search term
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filteredList = filteredList.filter(({career, category}) =>
                career.toLowerCase().includes(term) ||
                category.toLowerCase().includes(term)
            );
        }

        return filteredList;
    };

    return (
        <Layout>
            <div className="bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 font-[poppins] min-h-full">
                <div className="max-w-6xl mx-auto px-6 py-12">
                    {/* Welcome Section */}
                    {currentSection === 'welcome' && (
                        <div className="text-center mb-12">
                            <div className="mb-6">
                                <div
                                    className="w-20 h-20 bg-gradient-to-r from-[#f97316] to-[#ea580c] rounded-full flex items-center justify-center mx-auto mb-4 bounce-in float glow">
                                    <Compass className="w-10 h-10 text-white rotate-in"/>
                                </div>
                                <h1 className="text-5xl font-bold text-gray-800 mb-4 slide-in-left">Career Compass</h1>
                                <div
                                    className="w-24 h-1 bg-gradient-to-r from-[#f97316] to-[#ea580c] mx-auto rounded-full mb-6 scale-in"></div>
                            </div>
                            <h2 className="text-3xl font-semibold text-gray-700 mb-4 slide-in-right">Find Your Perfect
                                Career Path</h2>
                            <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed fade-in"
                               style={{animationDelay: '0.3s'}}>
                                Designed specifically for students in grades 10-12. Answer questions about your academic
                                interests, future goals, and work preferences to discover career paths that align with
                                your
                                strengths and aspirations.
                            </p>
                            <div
                                className="bg-white rounded-2xl shadow-lg p-6 max-w-md mx-auto mb-8 card-hover-effect bounce-in"
                                style={{animationDelay: '0.5s'}}>
                                <div className="flex items-center justify-center space-x-6 text-sm text-gray-600">
                                    <div className="text-center scale-in" style={{animationDelay: '0.7s'}}>
                                        <div className="text-2xl mb-1 float" style={{animationDelay: '0.5s'}}>📚</div>
                                        <div>15 Questions</div>
                                    </div>
                                    <div className="text-center scale-in" style={{animationDelay: '0.8s'}}>
                                        <div className="text-2xl mb-1 float" style={{animationDelay: '1s'}}>⏱️</div>
                                        <div>5 Minutes</div>
                                    </div>
                                    <div className="text-center scale-in" style={{animationDelay: '0.9s'}}>
                                        <div className="text-2xl mb-1 float" style={{animationDelay: '1.5s'}}>🎯</div>
                                        <div>250+ Careers</div>
                                    </div>
                                </div>
                            </div>
                            <button
                                onClick={startAssessment}
                                className="bg-gradient-to-r from-[#f97316] to-[#ea580c] hover:from-[#ea580c] hover:to-[#dc2626] text-white px-10 py-4 rounded-xl font-semibold text-lg transition-all pulse-animation shadow-lg glow scale-in"
                                style={{animationDelay: '1s'}}
                            >
                                Start Your Career Discovery
                            </button>
                        </div>
                    )}

                    {/* Assessment Section */}
                    {currentSection === 'assessment' && (
                        <div>
                            {/* Progress Bar */}
                            <div className="mb-8 fade-in">
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-sm font-medium text-gray-700 slide-in-left">Progress</span>
                                    <span className="text-sm font-medium text-gray-700 slide-in-right">
                  <span>{currentQuestion + 1}</span>/15
                </span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                                    <div
                                        className="bg-gradient-to-r from-[#f97316] to-[#ea580c] h-3 rounded-full progress-fill relative"
                                        style={{
                                            width: `${progressWidth}%`,
                                            transition: 'width 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
                                        }}
                                    >
                                        <div className="absolute inset-0 shimmer"></div>
                                    </div>
                                </div>
                            </div>

                            {/* Question Card */}
                            <div className="bg-white rounded-2xl shadow-xl p-8 mb-8 card-hover-effect bounce-in">
                                <h3 className={`text-2xl font-semibold text-gray-800 mb-6 ${typingAnimation ? 'typing-animation' : ''}`}>
                                    {questions[currentQuestion].text}
                                </h3>
                                <div className="space-y-3">
                                    {questions[currentQuestion].options.map((option, index) => (
                                        <div
                                            key={index}
                                            className={`cursor-pointer p-4 border-2 ${
                                                answers[currentQuestion] === index
                                                    ? 'border-orange-500 bg-orange-50'
                                                    : 'border-gray-200 hover:border-orange-500 hover:bg-orange-50'
                                            } rounded-lg transition-all duration-300 hover:scale-102`}
                                            onClick={() => selectOption(index)}
                                            style={{animationDelay: `${index * 0.1}s`}}
                                        >
                                            <label className="cursor-pointer flex items-center">
                                                <input
                                                    type="radio"
                                                    name={`question${currentQuestion}`}
                                                    value={index}
                                                    checked={answers[currentQuestion] === index}
                                                    onChange={() => {
                                                    }} // React requires an onChange handler
                                                    className="mr-3 text-orange-600"
                                                />
                                                <span className="text-gray-700">{option}</span>
                                            </label>
                                        </div>
                                    ))}
                                </div>
                                <div className="flex justify-between mt-8">
                                    <button
                                        onClick={previousQuestion}
                                        disabled={currentQuestion === 0}
                                        className={`px-6 py-3 ${
                                            currentQuestion === 0
                                                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300 cursor-pointer'
                                        } rounded-lg font-medium transition-all duration-300`}
                                    >
                                        <ArrowLeft className="inline-block w-4 h-4 mr-2"/>
                                        Previous
                                    </button>
                                    <button
                                        onClick={nextQuestion}
                                        disabled={answers[currentQuestion] === undefined}
                                        className={`px-6 py-3 ${
                                            answers[currentQuestion] === undefined
                                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                : 'bg-gradient-to-r from-[#f97316] to-[#ea580c] hover:from-[#ea580c] hover:to-[#dc2626] text-white cursor-pointer'
                                        } rounded-lg font-medium transition-all duration-300`}
                                    >
                                        {currentQuestion === questions.length - 1 ? 'Get Results' : 'Next'}
                                        <ArrowRight className="inline-block w-4 h-4 ml-2"/>
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Results Section */}
                    {currentSection === 'results' && (
                        <div>
                            <div className="text-center mb-8">
                                <h2 className="text-3xl font-bold text-gray-800 mb-4 slide-in-left">Your Career
                                    Recommendations</h2>
                                <p className="text-lg text-gray-600 slide-in-right">Based on your responses, here are
                                    the
                                    careers that match your profile:</p>
                            </div>

                            {/* Match Score */}
                            <div
                                className="bg-white rounded-2xl shadow-xl p-6 mb-8 text-center card-hover-effect bounce-in glow">
                                <div className="text-6xl mb-4 rotate-in float">🎯</div>
                                <h3 className="text-2xl font-bold text-gray-800 mb-2 fade-in">Your Career Match
                                    Score</h3>
                                <div
                                    className="text-4xl font-bold text-orange-600 scale-in"
                                    style={{
                                        transform: currentDisplayScore % 25 === 0 ? 'scale(1.1)' : 'scale(1)',
                                        color: currentDisplayScore >= matchScore ? '#f97316' : '',
                                        transition: 'transform 0.2s ease, color 0.3s ease'
                                    }}
                                >
                                    {currentDisplayScore}%
                                </div>
                                <p className="text-gray-600 mt-2 fade-in">Compatibility with top recommendations</p>
                                <div className="mt-4 w-full bg-gray-200 rounded-full h-2">
                                    <div
                                        className="bg-gradient-to-r from-[#f97316] to-[#ea580c] h-2 rounded-full transition-all duration-2000 ease-out"
                                        style={{width: `${currentDisplayScore}%`}}
                                    ></div>
                                </div>
                            </div>

                            {/* Career Recommendations */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                                {sortedCareers.map(([career, score], index) => {
                                    const category = findCareerCategory(career);
                                    const matchPercentage = Math.round((score / sortedCareers[0][1]) * 100);
                                    const careerInfo = careerDetails[career] || {
                                        description: `Exciting career opportunity in ${category.toLowerCase()}`,
                                        qualifications: "Requirements vary by position",
                                        workEnvironment: "Professional environment",
                                        industryOutlook: "Good prospects in growing field"
                                    };

                                    return (
                                        <div
                                            key={career}
                                            className="bg-white rounded-xl shadow-lg p-6 h-80 transition-all duration-600 transform hover:scale-[1.02] hover:shadow-xl"
                                            style={{
                                                opacity: 1,
                                                transform: 'translateY(0)',
                                                transition: `all 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275) ${index * 0.15}s`,
                                                position: 'relative',
                                                overflow: 'hidden'
                                            }}
                                        >
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="text-2xl float"
                                                     style={{animationDelay: `${index * 0.2}s`}}>
                                                    {getCareerEmoji(category)}
                                                </div>
                                                <div className="text-sm font-semibold text-orange-600 scale-in"
                                                     style={{animationDelay: `${index * 0.2 + 0.3}s`}}>
                                                    {matchPercentage}% Match
                                                </div>
                                            </div>
                                            <h3 className="text-lg font-semibold text-gray-800 mb-2 slide-in-left"
                                                style={{animationDelay: `${index * 0.2 + 0.1}s`}}>
                                                {career}
                                            </h3>
                                            <p className="text-sm text-gray-600 mb-2 slide-in-right"
                                               style={{animationDelay: `${index * 0.2 + 0.2}s`}}>
                                                {category}
                                            </p>
                                            <p className="text-xs text-gray-700 mb-3 leading-relaxed fade-in"
                                               style={{animationDelay: `${index * 0.2 + 0.4}s`}}>
                                                {careerInfo.description.substring(0, 120)}...
                                            </p>
                                            <div className="space-y-1 mb-3">
                                                <div className="text-xs text-gray-600 fade-in"
                                                     style={{animationDelay: `${index * 0.2 + 0.5}s`}}>
                                                    <span
                                                        className="font-medium text-orange-600">🎓 Qualifications:</span>
                                                    <p className="mt-1 leading-tight">
                                                        {careerInfo.qualifications ? careerInfo.qualifications.substring(0, 100) + '...' : 'Requirements vary by position'}
                                                    </p>
                                                </div>
                                                <div className="text-xs text-gray-600 fade-in"
                                                     style={{animationDelay: `${index * 0.2 + 0.6}s`}}>
                                                    <span
                                                        className="font-medium text-orange-600">💼 Work Environment:</span>
                                                    <p className="mt-1 leading-tight">
                                                        {careerInfo.workEnvironment ? careerInfo.workEnvironment.substring(0, 80) + '...' : 'Professional environment'}
                                                    </p>
                                                </div>
                                                <div className="text-xs text-gray-600 fade-in"
                                                     style={{animationDelay: `${index * 0.2 + 0.7}s`}}>
                                                    <span
                                                        className="font-medium text-orange-600">📈 Industry Outlook:</span>
                                                    <p className="mt-1 leading-tight">
                                                        {careerInfo.industryOutlook ? careerInfo.industryOutlook.substring(0, 80) + '...' : 'Good prospects in growing field'}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                                                <div
                                                    className="bg-gradient-to-r from-[#f97316] to-[#ea580c] h-2 rounded-full transition-all duration-1500 ease-out relative"
                                                    style={{width: `${matchPercentage}%`}}
                                                >
                                                    <div className="absolute inset-0 shimmer"></div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Action Buttons */}
                            <div className="text-center space-x-4 fade-in">
                                <button
                                    onClick={retakeAssessment}
                                    className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-all duration-300 hover:scale-105"
                                >
                                    Retake Assessment
                                </button>
                                <button
                                    onClick={exploreAllCareers}
                                    className="px-6 py-3 bg-gradient-to-r from-[#f97316] to-[#ea580c] hover:from-[#ea580c] hover:to-[#dc2626] text-white rounded-lg font-medium transition-all duration-300 hover:scale-105 glow"
                                >
                                    Explore All Careers
                                </button>
                            </div>
                        </div>
                    )}

                    {/* All Careers Section */}
                    {currentSection === 'allCareers' && (
                        <div>
                            <div className="text-center mb-8">
                                <h2 className="text-3xl font-bold text-gray-800 mb-4">All Career Options</h2>
                                <p className="text-lg text-gray-600 mb-6">Browse through 250+ career options organized
                                    by
                                    category</p>

                                {/* Search and Filter */}
                                <div className="max-w-2xl mx-auto mb-8">
                                    <div className="relative">
                                        <input
                                            type="text"
                                            value={searchTerm}
                                            onChange={handleSearchChange}
                                            placeholder="Search careers..."
                                            className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                        />
                                        <Search className="absolute left-3 top-3.5 w-4 h-4 text-gray-400"/>
                                    </div>
                                </div>

                                {/* Category Filter */}
                                <div className="flex flex-wrap justify-center gap-2 mb-8">
                                    <button
                                        onClick={() => filterByCategory('all')}
                                        className={`px-4 py-2 ${
                                            selectedCareerCategory === 'all'
                                                ? 'bg-gradient-to-r from-[#f97316] to-[#ea580c] text-white'
                                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                        } rounded-full text-sm font-medium transition-colors`}
                                    >
                                        All
                                    </button>
                                    {Object.keys(careers).map((category) => (
                                        <button
                                            key={category}
                                            onClick={() => filterByCategory(category)}
                                            className={`px-4 py-2 ${
                                                selectedCareerCategory === category
                                                    ? 'bg-gradient-to-r from-[#f97316] to-[#ea580c] text-white'
                                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                            } rounded-full text-sm font-medium transition-colors`}
                                        >
                                            {category}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Careers Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                {getFilteredCareers().map(({career, category}) => {
                                    const careerInfo = careerDetails[career] || {
                                        description: `Exciting career opportunity in ${category.toLowerCase()}`,
                                        qualifications: "Requirements vary by position",
                                        industryOutlook: "Good prospects in growing field"
                                    };

                                    return (
                                        <div
                                            key={career}
                                            className="bg-white rounded-lg shadow-md p-4 hover:shadow-lg transition-all cursor-pointer h-48"
                                        >
                                            <div className="text-2xl mb-2">{getCareerEmoji(category)}</div>
                                            <h3 className="font-semibold text-gray-800 text-sm mb-1">{career}</h3>
                                            <p className="text-xs text-gray-600 mb-2">{category}</p>
                                            <p className="text-xs text-gray-700 mb-2 leading-tight line-clamp-2">
                                                {careerInfo.description.substring(0, 80)}...
                                            </p>
                                            <div className="text-xs text-gray-600 space-y-1">
                                                <div className="truncate">
                                                    🎓 {careerInfo.qualifications ? careerInfo.qualifications.substring(0, 50) + '...' : 'Requirements vary'}
                                                </div>
                                                <div className="truncate">
                                                    📈 {careerInfo.industryOutlook ? careerInfo.industryOutlook.substring(0, 50) + '...' : 'Good prospects'}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="text-center mt-8">
                                <button
                                    onClick={goBackToResults}
                                    className="px-6 py-3 bg-gradient-to-r from-[#f97316] to-[#ea580c] hover:from-[#ea580c] hover:to-[#dc2626] text-white rounded-lg font-medium transition-colors"
                                >
                                    Back to Results
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default CareerMeterComponent;